/**
 * 自分のアイコンを変える（船員アプリ・個人アプリ共通）。
 * アイコンを押す →「写真を撮る／選ぶ」→ 丸く切り抜いたプレビューで回転・確認 → 保存。
 * スマホの写真は数MBあり、そのままだとサーバーの上限(5MB)を超えたり通信が重いので、
 * 端末側で中央を正方形に切り抜き 512px の JPEG に縮めてから送る。
 */
import React, { useRef, useState } from 'react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { saveMyAvatar } from '../services/api';
import { updateStoredUser, type AuthUser } from '../hooks/useAuth';

const OUT_PX = 512;

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('この画像は読み込めませんでした')); };
    img.src = url;
  });
}

/** 中央を正方形に切り抜き、rot(0/90/180/270)だけ回して OUT_PX の JPEG にする */
function renderSquare(img: HTMLImageElement, rot: number): Promise<Blob> {
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;
  const cv = document.createElement('canvas');
  cv.width = OUT_PX; cv.height = OUT_PX;
  const ctx = cv.getContext('2d');
  if (!ctx) return Promise.reject(new Error('画像の処理ができませんでした'));
  ctx.translate(OUT_PX / 2, OUT_PX / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(img, sx, sy, side, side, -OUT_PX / 2, -OUT_PX / 2, OUT_PX, OUT_PX);
  return new Promise((resolve, reject) =>
    cv.toBlob(b => (b ? resolve(b) : reject(new Error('画像の処理ができませんでした'))), 'image/jpeg', 0.85));
}

interface Props {
  user: AuthUser;
  size: number;
  /** 画像が無いときの丸（イニシャル）の見た目を上書きする */
  fallbackStyle?: React.CSSProperties;
}

export const MyAvatarEditor: React.FC<Props> = ({ user, size, fallbackStyle }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rot, setRot] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // 運営管理者の確認用ログインは会社のユーザー表に存在しないので変更させない
  const editable = !!user.user_id && localStorage.getItem('catms_is_super_admin') !== '1';

  const refreshPreview = async (im: HTMLImageElement, r: number) => {
    const b = await renderSquare(im, r);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // 同じ写真を選び直せるように
    if (!f) return;
    setMsg(null);
    try {
      const im = await loadImage(f);
      setImg(im); setRot(0);
      await refreshPreview(im, 0);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '画像を読み込めませんでした');
    }
  };

  const close = () => {
    setImg(null);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
  };

  const rotate = async () => {
    if (!img) return;
    const r = (rot + 90) % 360;
    setRot(r);
    await refreshPreview(img, r);
  };

  const save = async () => {
    if (!img) return;
    setBusy(true); setMsg(null);
    try {
      const path = await saveMyAvatar(user.user_id, await renderSquare(img, rot));
      updateStoredUser({ avatar_path: path });
      close();
      setMsg('✅ アイコンを変更しました');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '保存できませんでした');
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('アイコンの写真を消して、名前の頭文字に戻しますか？')) return;
    setBusy(true); setMsg(null);
    try {
      await saveMyAvatar(user.user_id, null);
      updateStoredUser({ avatar_path: null });
      setMsg('アイコンを元に戻しました');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '削除できませんでした');
    }
    setBusy(false);
  };

  const circle: React.CSSProperties = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 };
  const badge = Math.max(20, Math.round(size * 0.36));
  const fallback = (
    <div style={{
      ...circle, background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.42), fontWeight: 700, color: '#0ea5e9', ...fallbackStyle,
    }}>
      {user.user_name.charAt(0)}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <button type="button" disabled={!editable || busy} onClick={() => fileRef.current?.click()}
        aria-label="アイコンを変更する"
        style={{ position: 'relative', padding: 0, border: 'none', background: 'none', cursor: editable ? 'pointer' : 'default', borderRadius: '50%' }}>
        <AuthenticatedImage resource={user.avatar_path ? `avatars/${user.avatar_path}` : null} alt={user.user_name} imgStyle={circle} fallback={fallback} />
        {editable && (
          <span style={{
            position: 'absolute', right: -2, bottom: -2, width: badge, height: badge, borderRadius: '50%',
            background: '#0057d9', color: '#fff', border: '2px solid #fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: Math.max(11, Math.round(size * 0.18)),
          }}>📷</span>
        )}
      </button>
      {editable && user.avatar_path && (
        <button type="button" onClick={remove} disabled={busy}
          style={{ border: 'none', background: 'none', color: '#94a3b8', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }}>
          写真を消す
        </button>
      )}
      {msg && (
        <div style={{ fontSize: 11.5, color: msg.startsWith('✅') ? '#059669' : '#b45309', maxWidth: 220, textAlign: 'center', lineHeight: 1.4 }}>
          {msg}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPick} />

      {img && previewUrl && (
        <div onClick={busy ? undefined : close}
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 18, padding: '22px 20px 18px', width: '100%', maxWidth: 340, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 14 }}>この写真をアイコンにしますか？</div>
            <img src={previewUrl} alt="プレビュー"
              style={{ width: 180, height: 180, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }} />
            <div style={{ fontSize: 12, color: '#64748b', margin: '10px 0 14px' }}>写真の真ん中が丸く切り抜かれます</div>
            <button type="button" onClick={rotate} disabled={busy}
              style={{ width: '100%', padding: '11px 0', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 14, fontWeight: 700, marginBottom: 8, cursor: 'pointer' }}>
              ↻ 向きを回す
            </button>
            <button type="button" onClick={save} disabled={busy}
              style={{ width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', background: busy ? '#94a3b8' : '#0057d9', color: '#fff', fontSize: 15, fontWeight: 800, marginBottom: 8, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? '保存しています…' : 'この写真にする'}
            </button>
            <button type="button" onClick={close} disabled={busy}
              style={{ width: '100%', padding: '10px 0', borderRadius: 12, border: 'none', background: 'none', color: '#64748b', fontSize: 14, cursor: 'pointer' }}>
              やめる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAvatarEditor;
